import { Utils } from './Utils';
import { OrderInfo, ReturnsInfo } from '../models/models';
import { Store } from '../models/RestModels';

export type ReturnReceiptVariant = 'coupon' | 'money';

/**
 * Display-ready return receipt data as ESC/POS XML.
 *
 * Mirrors PrintReturnReceiptComponent
 * (src/app/pages/print-return-receipt) and
 * PrintCancellationItemReceiptComponent
 * (src/app/pages/print-cancellation-item-receipt) so the USB ticket prints
 * the same items and totals as the browser receipt.
 * Variant 'coupon' is the credit-note text with code,
 * variant 'money' is the cash-refund text with signature.
 */
export interface ReturnReceiptTicketData {
	returns_info: ReturnsInfo;
	order_info: OrderInfo | null;
	store: Store;
	variant: ReturnReceiptVariant;
}

export class ReturnReceiptTicketBuilder {

	private data: ReturnReceiptTicketData;

	ticket_a_columns: number = 42;
	ticket_b_columns: number = 50;
	font_row: string = 'b';

	constructor(data: ReturnReceiptTicketData, ticket_a_columns: number = 42, ticket_b_columns: number = 50) {
		this.data = data;
		this.ticket_a_columns = ticket_a_columns;
		this.ticket_b_columns = ticket_b_columns;
	}

	private getEncoderMaxColumns(font_name: 'a' | 'b' = 'b'): number {
		return font_name == 'a' ? this.ticket_a_columns : this.ticket_b_columns;
	}

	private escapeXml(str: string): string {
		if (!str) return '';
		return str
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&apos;');
	}

	private formatMoney(amount: number | null | undefined): string {
		return Utils.formatMoney(amount || 0);
	}

	private formatShortDate(value: any): string {
		return Utils.getDateString(value, true);
	}

	private buildSectionTitle(title: string): string {
		return `<Text justification="center" bold="true">${this.escapeXml(title)}</Text>\n<Feed lines="1"></Feed>\n`;
	}

	private buildTotalsRow(label: string, value: string, maxCols: number, bold: boolean = false): string {
		const dots = '.'.repeat(Math.max(0, maxCols - label.length - value.length - 2));

		if (bold) {
			return `<Text font="a" bold="true">${this.escapeXml(label)} ${dots} ${this.escapeXml(value)}</Text><Feed lines="1"></Feed>\n`;
		}
		return `<Text font="a">${this.escapeXml(label)} ${dots} ${this.escapeXml(value)}</Text><Feed lines="1"></Feed>\n`;
	}

	public buildXml(): string {
		let xml = '<EscPos>\n';
		xml += '<Init></Init>\n';
		xml += this.buildHeader();
		xml += this.buildBody();
		xml += this.buildFooter();
		xml += '<Feed lines="2"></Feed>\n';
		xml += '<Cut mode="partial"></Cut>\n';
		xml += '</EscPos>';
		return xml;
	}

	private buildHeader(): string {
		let xml = '';
		const store = this.data.store;

		if (store?.business_name) {
			xml += `<Text justification="center" bold="true">${this.escapeXml(store.business_name)}</Text>\n`;
		}
		if (store?.name) {
			xml += `<Text justification="center">${this.escapeXml(store.name)}</Text>\n`;
		}
		const parts = [store?.address, store?.city, store?.phone]
			.filter(p => !!p)
			.map(p => this.escapeXml(p as string));
		if (parts.length) {
			xml += `<Text justification="center">${parts.join(' ')}</Text>\n`;
		}
		xml += '<Feed lines="1"></Feed>\n';

		const returns = this.data.returns_info?.returns;
		const title = this.data.variant === 'money'
			? `Recibo de Devolucion Folio #${returns?.id ?? ''}`
			: `Nota de Credito Folio #${returns?.id ?? ''}`;
		xml += this.buildSectionTitle(title);
		return xml;
	}

	private unitPrice(returned_qty: number | null | undefined, total: number | null | undefined): number {
		const qty = returned_qty || 0;
		if (!qty) return total || 0;
		return (total || 0) / qty;
	}

	private itemLabel(category_name: string | null | undefined, item_name: string | null | undefined): string {
		return `${category_name || ''} ${item_name || ''}`.trim();
	}

	private buildBody(): string {
		let xml = '';
		const returns = this.data.returns_info?.returns;
		const order_id = returns?.order_id ?? this.data.order_info?.order?.id ?? '';

		xml += `<Text font="b" justification="left">Venta Folio: </Text><Text font="b" bold="true" justification="left">#${this.escapeXml(`${order_id}`)}</Text><Feed lines="1"></Feed>\n`;
		if (returns?.created) {
			xml += `<Text font="b" justification="left">Fecha: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(this.formatShortDate(returns.created))}</Text><Feed lines="1"></Feed>\n`;
		}
		if (this.data.variant === 'coupon' && returns?.code) {
			xml += `<Text font="b" justification="left">Codigo: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(returns.code)}</Text><Feed lines="1"></Feed>\n`;
		}
		xml += '<Feed lines="1"></Feed>\n';
		xml += this.buildItemsTable();

		const maxCols = this.getEncoderMaxColumns('a');
		xml += this.buildTotalsRow('Total', this.formatMoney(returns?.total), maxCols, true);
		return xml;
	}

	private buildItemsTable(): string {
		const maxCols = this.getEncoderMaxColumns('b');
		const qtyWidth = 10;
		const totalWidth = 11;
		const articleWidth = Math.max(10, maxCols - qtyWidth - totalWidth - 2);

		let columnsXml = '<Columns>\n';
		columnsXml += `<Column width="${articleWidth}" align="left" marginRight="1"></Column>\n`;
		columnsXml += `<Column width="${qtyWidth}" align="right" marginRight="1"></Column>\n`;
		columnsXml += `<Column width="${totalWidth}" align="right"></Column>\n`;
		columnsXml += '</Columns>\n';

		let rowsXml = '<Row>\n';
		rowsXml += '<Cell><Text bold="true">Articulo</Text></Cell>\n';
		rowsXml += '<Cell><Text bold="true">Cant</Text></Cell>\n';
		rowsXml += '<Cell><Text bold="true">Total</Text></Cell>\n';
		rowsXml += '</Row>\n';
		rowsXml += this.buildSeparatorRow();

		for (const rii of this.data.returns_info?.items || []) {
			const qty = rii.returned_item?.returned_qty || 0;
			const total = rii.returned_item?.total || 0;
			const unit = this.unitPrice(qty, total);
			const qtyText = `${qty}x${this.formatMoney(unit)}`;
			rowsXml += '<Row>\n';
			rowsXml += `<Cell><Text>${this.escapeXml(this.itemLabel(rii.category?.name, rii.item?.name))}</Text></Cell>\n`;
			rowsXml += `<Cell><Text>${this.escapeXml(qtyText)}</Text></Cell>\n`;
			rowsXml += `<Cell><Text>${this.escapeXml(this.formatMoney(total))}</Text></Cell>\n`;
			rowsXml += '</Row>\n';
			rowsXml += this.buildSeparatorRow();
		}

		return `<Table font="b">\n${columnsXml}<Rows>\n${rowsXml}</Rows>\n</Table>\n`;
	}

	private buildSeparatorRow(): string {
		let xml = '<Row>\n';
		xml += '<Cell><Rule/></Cell>\n';
		xml += '<Cell><Rule/></Cell>\n';
		xml += '<Cell><Rule/></Cell>\n';
		xml += '</Row>\n';
		return xml;
	}

	private buildFooter(): string {
		let xml = '<Feed lines="1"></Feed>\n';
		const returns = this.data.returns_info?.returns;
		const order_id = returns?.order_id ?? this.data.order_info?.order?.id ?? '';

		if (this.data.variant === 'money') {
			xml += `<Text justification="left">El cliente, acepta que se le devolvera la cantidad de $${this.escapeXml(`${returns?.total ?? ''}`)} Pesos correspondiente a los articulos devueltos de la venta con el folio: ${this.escapeXml(`${order_id}`)}.</Text><Feed lines="1"></Feed>\n`;
			xml += `<Text justification="left">Asimismo, el cliente declara estar de acuerdo en que esta devolucion se realiza conforme a las politicas de la empresa.</Text><Feed lines="1"></Feed>\n`;
			xml += '<Feed lines="2"></Feed>\n';
			xml += `<Text justification="center">_______________________________</Text>\n`;
			xml += `<Text justification="center" bold="true">Firma del cliente:</Text>\n`;
		} else {
			xml += `<Text justification="left">Esta nota de credito contiene el valor correspondiente a los articulos devueltos de la venta con el folio: ${this.escapeXml(`${order_id}`)}, y podras hacerlo valido en cualquiera de nuestras sucursales participantes.</Text><Feed lines="1"></Feed>\n`;
			if (returns?.code) {
				xml += `<Text justification="left">Codigo: </Text><Text bold="true" justification="left">${this.escapeXml(returns.code)}</Text><Feed lines="1"></Feed>\n`;
			}
		}
		xml += '<Feed lines="1"></Feed>\n';
		return xml;
	}
}
